// Imperative DOM/SVG rendering for the still-legacy surfaces:
// - process picker cards (#cards + Story scene 2's #story-cards)
// - process meta header (#nb-proc-name, #nb-proc-desc) in the notebook
// - today's tax + hero wins + portfolio rollup paint targets
// - step breakdown bars
// - histogram + histogram callout + failure-rate bar
// - story-mode lane animation (#story-svg-human / #story-svg-ai)
//
// Renamed from `render.js` to `story-render.js` in Phase 5 of the
// React-engine port (docs/eng/pilot-react-engine.md). The Sandbox's
// knob grid, race lanes, stat row, and FAB are React-owned now
// (see components/pilot/Sandbox.tsx). The surviving exports below
// are consumed by story.js (Phase 4 will lift them too) and the
// still-imperative parts of sandbox main.js (today's tax, hero
// wins, distribution, scale rollup — Phase 5+ territory).
//
// Format helpers (fmtUSD / fmtDays / animateNumberValue) live in
// `lib/pilot/format.ts`. The re-export shim this module used to
// carry for story.js is retired — Story.tsx imports them directly
// from `./format` now.

const $ = sel => document.querySelector(sel);

// Read live theme colors from CSS custom properties so SVG fills/strokes
// follow whatever theme is currently active.
function themeColors() {
  const cs = getComputedStyle(document.documentElement);
  const v = name => cs.getPropertyValue(name).trim();
  return {
    human:  v("--human")  || "#f59f4a",
    ai:     v("--ai")     || "#6dd6ff",
    brand:  v("--brand")  || "#ff5d8f",
    text:   v("--text")   || "#e7ecf6",
    dim:    v("--text-dim") || "#b9c2d8",
    muted:  v("--muted")  || "#8896b3",
    line:   v("--line-strong") || "rgba(255,255,255,0.16)",
    bgCard: v("--bg-1") || "rgba(255,255,255,0.02)",
  };
}

// `buildPickerCards` retired by Phase B step 2 of the imperative-
// renderer retirement (docs/eng/pilot-engine-imperative-retirement.md).
// The picker is now `<ProcessPicker>` in
// apps/web/src/components/pilot/ProcessPicker.tsx; its `.card*` CSS
// rules in pilot-engine.css are deletable in the same commit.

// ---------- Process intro ----------

// renderProcessMeta retired by Phase B step 3 of the imperative-
// renderer retirement. <SandboxIntroHeader> in Sandbox.tsx owns the
// `#nb-proc-name` / `#nb-proc-desc` paint; the meta row is a
// pre-existing React component (<SandboxStatRow>). main.js's
// loadProcess no longer calls renderProcessMeta — the React tree
// re-renders the header whenever PilotContext.currentProcessId
// changes (which happens via the `pilot:process-selected` event
// dispatched by loadProcess + subscribed in SandboxSections.tsx).

// kept for API compatibility; the notebook layout handles assumptions inline.
export function updateNotes() { /* no-op */ }

// buildKnobs was removed in Phase 5 of the React-engine port
// (docs/eng/pilot-react-engine.md). The Sandbox knob grid is now a
// React component (components/pilot/Sandbox.tsx#KnobGrid) backed by
// PilotContext state.

// `renderHeroWins` + `renderTodaysTax` retired by Phase C of the
// imperative-renderer retirement
// (docs/eng/pilot-engine-imperative-retirement.md). The Sandbox
// hero-wins trio + today's tax trio render from React via
// <SandboxHeroWins> + <SandboxTodaysTax> in Sandbox.tsx. They consume
// the same sim worker results + hourlyRate knob and animate via
// useCountUp's IntersectionObserver. `_winsObserver`, `_winsTargets`,
// `_winsArmed`, `_runWinsAnim`, `_ensureWinsObserver` deleted.

// ---------- Step breakdown ("where the time goes") ----------
// Two stacked horizontal timelines (today vs Planapus) where each segment is
// a single handoff. Width = that handoff's share of total wall-clock time.
// Replaces the previous one-row-per-step layout that ate 800px of vertical.
// The point: at a glance you can SEE which segments collapse under Planapus.

// `renderStepBreakdown` (+ shortLabel + fmtStepHrs helpers) retired by
// Phase D of the imperative-renderer retirement
// (docs/eng/pilot-engine-imperative-retirement.md). <SandboxStepBreakdown>
// in Sandbox.tsx subscribes to useSimWorker results.breakdown.{human,ai}
// and renders the two stacked bars + biggest-cuts chips inline-Tailwind.
// The `.nb-steps-*` / `.nb-step-seg*` CSS rules are deleted in the
// same commit.

// ---------- Histogram callout (win probability + failure rate) ----------

// renderHistogramCallout retired by Phase E of the imperative-renderer
// retirement (docs/eng/pilot-engine-imperative-retirement.md).
// <SandboxDistHeadline> + <SandboxFailbar> in Sandbox.tsx subscribe to
// useSimWorker `results.winProb` + `results.human.failRate` +
// `results.ai.failRate` and render inline-Tailwind. The .nb-failbar-*
// CSS rules retire in the same commit.

// renderPortfolioWins (R5b) retired by Phase E (rest) of the
// imperative-renderer retirement. The Sandbox portfolio rollup is now
// React-owned in components/pilot/Sandbox.tsx#SandboxPortfolioWins,
// which calls runMonteCarloSummary directly and renders chips +
// totals via JSX.


// ---------- Histogram ----------

export function renderHistogram(human, ai, opts = {}) {
  const svgId = opts.svgId || "svg-histo";
  const svg = document.getElementById(svgId);
  if (!svg) return;
  const colors = themeColors();
  const W = 800, H = 220, PAD_L = 48, PAD_R = 28, PAD_T = 26, PAD_B = 24;
  const allMax = Math.max(
    ...(human.days.length ? [human.days[human.days.length - 1]] : [10]),
    ...(ai.days.length ? [ai.days[ai.days.length - 1]] : [10]),
    Math.max(human.p90, ai.p90, 1)
  );
  const maxDay = Math.ceil(allMax * 1.05);
  const BINS = 40;

  const bin = arr => {
    const counts = new Array(BINS).fill(0);
    const w = maxDay / BINS;
    for (const d of arr) {
      const b = Math.min(BINS - 1, Math.floor(d / w));
      counts[b]++;
    }
    const max = Math.max(...counts, 1);
    return { counts, max, binW: w };
  };
  const h = bin(human.days);
  const a = bin(ai.days);
  const peak = Math.max(h.max, a.max);

  const chartW = W - PAD_L - PAD_R, chartH = H - PAD_T - PAD_B;
  const xScale = i => PAD_L + (i + 0.5) / BINS * chartW;
  const yScale = c => PAD_T + chartH - (c / peak) * chartH;

  const path = counts => {
    let d = `M ${PAD_L} ${PAD_T + chartH} `;
    for (let i = 0; i < BINS; i++) d += `L ${xScale(i)} ${yScale(counts[i])} `;
    d += `L ${PAD_L + chartW} ${PAD_T + chartH} Z`;
    return d;
  };

  // Round, regular x-ticks so the axis doesn't end on weird fractions.
  // Pick a step that gives ~6-8 ticks across the data range.
  const niceStep = (() => {
    const target = maxDay / 7;
    const candidates = [1, 2, 5, 7, 10, 14, 21, 28, 30, 60, 90];
    for (const c of candidates) if (c >= target) return c;
    return Math.ceil(target / 10) * 10;
  })();
  const xTicks = [];
  for (let day = 0; day <= maxDay; day += niceStep) {
    const x = PAD_L + (day / maxDay) * chartW;
    xTicks.push(`<text x="${x}" y="${H - 6}" fill="${colors.muted}" font-size="10" text-anchor="middle">${day}d</text>`);
  }

  // PLN-312 — Y-axis tick scale. Without these the reader can see one
  // curve is taller than the other but can't anchor it to a number. Each
  // bin's count is divided by total trials on the *taller* series so the
  // peak label reads as "X% of that series' runs landed here".
  const peakSeriesTotal = (h.max >= a.max ? human.days.length : ai.days.length) || 1;
  const peakPct = peak / peakSeriesTotal;
  const yTicks = [];
  // Pick 3 readable reference fractions: 0, mid, peak.
  const fractions = [0, 0.5, 1];
  for (const f of fractions) {
    const yPx = PAD_T + chartH - f * chartH;
    const pct = peakPct * f * 100;
    const label = f === 0 ? "0%" : `${pct.toFixed(pct < 1 ? 2 : pct < 10 ? 1 : 0)}%`;
    // Faint baseline tick + label to the left of PAD_L.
    yTicks.push(`<line x1="${PAD_L}" y1="${yPx}" x2="${PAD_L + chartW}" y2="${yPx}" stroke="${colors.muted}" stroke-width="0.5" stroke-dasharray="2 4" opacity="0.25"/>`);
    yTicks.push(`<text x="${PAD_L - 6}" y="${yPx + 3}" fill="${colors.muted}" font-size="10" text-anchor="end">${label}</text>`);
  }

  // Collision-aware label placement. When two series' medians (or P90s) sit
  // within roughly one label-width of each other, both right-anchored labels
  // would overlap on the same baseline. Push the rightmost one down so each
  // line has a clear annotation.
  const xFor = (val) => PAD_L + (val / maxDay) * chartW;
  const MEDIAN_MIN_GAP_PX = 100; // approx max width of "Planapus: 12.3d"
  const P90_MIN_GAP_PX = 80;
  const humanMedianX = xFor(human.median);
  const aiMedianX = xFor(ai.median);
  const medianCollide = Math.abs(humanMedianX - aiMedianX) < MEDIAN_MIN_GAP_PX;
  const humanP90X = xFor(human.p90);
  const aiP90X = xFor(ai.p90);
  const p90Collide = Math.abs(humanP90X - aiP90X) < P90_MIN_GAP_PX;
  // When colliding: the leftmost stays on baseline, the rightmost drops a row.
  const medianYHuman = medianCollide && humanMedianX > aiMedianX ? PAD_T + 30 : PAD_T + 14;
  const medianYAi    = medianCollide && aiMedianX > humanMedianX ? PAD_T + 30 : PAD_T + 14;
  const p90YHuman = p90Collide && humanP90X > aiP90X ? PAD_T + chartH - 22 : PAD_T + chartH - 6;
  const p90YAi    = p90Collide && aiP90X > humanP90X ? PAD_T + chartH - 22 : PAD_T + chartH - 6;

  // Median: bigger, with a leader-dot at the curve top, always visible.
  const medianLine = (val, color, label, y) => {
    const x = xFor(val);
    return `
      <line x1="${x}" y1="${PAD_T}" x2="${x}" y2="${PAD_T + chartH}" stroke="${color}" stroke-dasharray="5 4" stroke-width="1.5" opacity="0.9"/>
      <text x="${x + 6}" y="${y}" fill="${color}" font-size="13" font-weight="700">${label}: ${val.toFixed(1)}d</text>
    `;
  };
  // P90 stays always-visible (mobile + screenshots), but the hover layer
  // amplifies it: bigger text, fuller-opacity line, and a tooltip readout.
  // The text uses `paint-order: stroke` + a thick bg-cream halo so the label
  // is legible over the colored area fill regardless of where it lands —
  // direct AI-stroke colour against cream is below the 4.5:1 AA threshold
  // (e.g. branded `#ff8aa3` ≈ 2.3:1), so we lean on the halo + bumped size
  // and weight to keep it readable without changing the brand palette.
  const haloColor = (getComputedStyle(document.documentElement).getPropertyValue("--bg").trim()) || "#F7F1E0";
  const p90Line = (val, color, seriesCls, y) => {
    const x = xFor(val);
    return `
      <g class="p90-mark ${seriesCls}-p90" data-series="${seriesCls}" data-p90="${val.toFixed(1)}" data-x="${x}">
        <line x1="${x}" y1="${PAD_T}" x2="${x}" y2="${PAD_T + chartH}" stroke="${color}" stroke-dasharray="2 4" stroke-width="1" opacity="0.7"/>
        <text x="${x + 4}" y="${y}" fill="${color}" font-size="12" font-weight="600" paint-order="stroke" stroke="${haloColor}" stroke-width="3" stroke-linejoin="round" stroke-opacity="0.95">P90: ${val.toFixed(1)}d</text>
      </g>
    `;
  };

  // Y-axis hint at top-left so the reader knows what the height means.
  // PLN-311 — was 10px italic muted, easy to miss. Bumped to 12px /
  // weight 500 / secondary text color (`--text-dim` ≈ rgb(73, 71, 63))
  // so it scans as a label, not a watermark.
  const yHint = `
    <text x="${PAD_L}" y="${PAD_T - 10}" fill="${colors.dim}" font-size="12" font-weight="500" font-style="italic">↑ how often a run lands here</text>
  `;

  // In Paper themes we want flat (non-gradient) fills. Detect by checking
  // the active theme attribute and emit either a gradient or a flat fill.
  const themeAttr = document.documentElement.getAttribute("data-theme") || "planapus";
  const flat = themeAttr.startsWith("paper");

  // The story-mode histogram uses brand-aligned (pink→orange softened) fills
  // for the Planapus series so it visually ties to the title gradient and the
  // inline "with Planapus" coloring. The sandbox histogram keeps its existing
  // cyan/orange palette so it stays consistent with the rest of the sandbox.
  const branded = !!opts.branded && !flat;
  const aiStroke = branded ? "#ff8aa3" : colors.ai;
  const aiTextColor = branded ? "#ff8aa3" : colors.ai;
  const humanFill = flat ? colors.human : "url(#gh)";
  const aiFill = flat ? colors.ai : (branded ? "url(#ga-branded)" : "url(#ga)");
  const humanFillOpacity = flat ? "0.18" : "1";
  const aiFillOpacity = flat ? "0.18" : "1";

  svg.setAttribute("viewBox", `0 0 ${W} ${H}`);
  svg.innerHTML = `
    <defs>
      <linearGradient id="gh" x1="0" x2="0" y1="0" y2="1">
        <stop offset="0%" stop-color="${colors.human}" stop-opacity="0.55"/>
        <stop offset="100%" stop-color="${colors.human}" stop-opacity="0.05"/>
      </linearGradient>
      <linearGradient id="ga" x1="0" x2="0" y1="0" y2="1">
        <stop offset="0%" stop-color="${colors.ai}" stop-opacity="0.55"/>
        <stop offset="100%" stop-color="${colors.ai}" stop-opacity="0.05"/>
      </linearGradient>
      <linearGradient id="ga-branded" x1="0" x2="1" y1="0" y2="1">
        <stop offset="0%"   stop-color="#ff8aa3" stop-opacity="0.60"/>
        <stop offset="60%"  stop-color="#ffa17a" stop-opacity="0.32"/>
        <stop offset="100%" stop-color="#f5d27a" stop-opacity="0.05"/>
      </linearGradient>
    </defs>
    <g class="series series-human" data-series="human" data-p90="${human.p90.toFixed(1)}" data-label="Today">
      <path d="${path(h.counts)}" fill="${humanFill}" fill-opacity="${humanFillOpacity}" stroke="${colors.human}" stroke-width="1.5"/>
      ${p90Line(human.p90, colors.human, "human", p90YHuman)}
    </g>
    <g class="series series-ai" data-series="ai" data-p90="${ai.p90.toFixed(1)}" data-label="With Planapus">
      <path d="${path(a.counts)}" fill="${aiFill}" fill-opacity="${aiFillOpacity}" stroke="${aiStroke}" stroke-width="1.5"/>
      ${p90Line(ai.p90, aiTextColor, "ai", p90YAi)}
    </g>
    ${medianLine(human.median, colors.human, "Today", medianYHuman)}
    ${medianLine(ai.median,    aiTextColor,  "Planapus", medianYAi)}
    ${yHint}
    ${yTicks.join("")}
    ${xTicks.join("")}
  `;

  // Hover/click interactivity intentionally disabled: the chart paints
  // P90 + median labels inline so the data is fully visible without
  // interaction. The prior `wireHistogramHover` re-bound listeners on
  // every repaint (every knob tick) which read as jumpy on hover/click.
  // Drop a stale tooltip sibling if a prior paint had one.
  const wrap = svg.parentElement;
  if (wrap) {
    const tip = wrap.querySelector(":scope > .histo-tip");
    if (tip) tip.remove();
  }
}

function wireHistogramHover(svg, ctx) {
  // Mount (once) a tooltip element as a sibling of the SVG so it can overflow
  // the SVG bounds cleanly. The parent `.histo` is positioned `relative`
  // (set in styles.css for the story scene).
  const wrap = svg.parentElement;
  if (!wrap) return;
  wrap.style.position = wrap.style.position || "relative";
  let tip = wrap.querySelector(":scope > .histo-tip");
  if (!tip) {
    tip = document.createElement("div");
    tip.className = "histo-tip";
    tip.hidden = true;
    wrap.appendChild(tip);
  }

  const seriesNodes = svg.querySelectorAll(".series");
  for (const g of seriesNodes) {
    const path = g.querySelector("path");
    if (!path) continue;
    path.style.cursor = "crosshair";
    path.style.transition = "fill-opacity 120ms ease, stroke-width 120ms ease";

    const onEnter = () => {
      // Dim the other series; amplify this one.
      for (const other of seriesNodes) {
        const otherPath = other.querySelector("path");
        const otherP90  = other.querySelector(".p90-mark");
        const isThis = other === g;
        if (otherPath) otherPath.style.fillOpacity = isThis ? "1" : "0.18";
        if (otherP90) otherP90.setAttribute("opacity", isThis ? "1" : "0.25");
      }
      path.setAttribute("stroke-width", "2.2");

      const which = g.dataset.series === "ai" ? ctx.ai : ctx.human;
      const x = ctx.xFor(which.p90);
      const svgRect = svg.getBoundingClientRect();
      const wrapRect = wrap.getBoundingClientRect();
      // Map SVG-viewBox x to actual rendered pixels for tooltip positioning.
      const renderedX = svgRect.left - wrapRect.left + (x / ctx.W) * svgRect.width;
      const renderedTop = svgRect.top - wrapRect.top + (ctx.PAD_T / ctx.H) * svgRect.height;

      // F17 Slice C: Tailwind utilities replace the prior `.histo-tip-*`
      // rules in pilot-engine.css. The leading legacy class names stay
      // as DOM-inspectable hooks but no CSS targets them anymore.
      tip.innerHTML = `
        <div class="histo-tip-label text-[11px] text-taupe-600" style="color:${which.color}">${escapeHtml(which.label)}</div>
        <div class="histo-tip-stat [font-family:Inter,ui-monospace,monospace] [font-feature-settings:'tnum'] font-semibold text-taupe-950"><b style="color:${which.color}">P90:</b> ${which.p90.toFixed(1)} days</div>
        <div class="histo-tip-hint text-[11px] text-taupe-600">9 of 10 runs finish by this point</div>
      `;
      tip.style.borderColor = which.color;
      tip.hidden = false;
      // Position the tip horizontally over the P90 column, vertically inside
      // the chart area near its top. Clamp horizontally so the tip can't
      // overflow the .histo box.
      const tipW = tip.offsetWidth || 180;
      const wrapW = wrap.clientWidth;
      let left = renderedX - tipW / 2;
      left = Math.max(8, Math.min(wrapW - tipW - 8, left));
      tip.style.left = `${left}px`;
      tip.style.top = `${renderedTop + 8}px`;
    };

    const onLeave = () => {
      for (const other of seriesNodes) {
        const otherPath = other.querySelector("path");
        const otherP90 = other.querySelector(".p90-mark");
        if (otherPath) otherPath.style.fillOpacity = "";
        if (otherP90) otherP90.setAttribute("opacity", "1");
      }
      path.setAttribute("stroke-width", "1.5");
      tip.hidden = true;
    };

    path.addEventListener("mouseenter", onEnter);
    path.addEventListener("mouseleave", onLeave);
    // Keyboard accessibility: focus the path (SVG <path> isn't focusable by
    // default; tabindex=0 makes it so) to trigger the readout.
    path.setAttribute("tabindex", "0");
    path.addEventListener("focus", onEnter);
    path.addEventListener("blur", onLeave);
  }
}

// ---------- Swim lanes ----------

function layoutNodes(parties, w, h, mode) {
  const cx = w / 2, cy = h / 2 + 10;
  const r = Math.min(w, h) * 0.40;
  const N = parties.length;
  const nodes = parties.map((p, i) => {
    const ang = -Math.PI / 2 + (i / N) * Math.PI * 2;
    return { ...p, x: cx + Math.cos(ang) * r, y: cy + Math.sin(ang) * r };
  });
  const hub = mode === "ai" ? { x: cx, y: cy } : null;
  return { nodes, hub, cx, cy, r };
}

// Set of party ids actually touched by a sampled trace.
function tracePartySet(trace) {
  const set = new Set();
  for (const step of trace) {
    set.add(step.from);
    set.add(step.to);
  }
  return set;
}

function renderLaneStatic(svgId, proc, layout, mode, involvedSet) {
  const svg = document.getElementById(svgId);
  const orgs = Object.fromEntries(proc.orgs.map(o => [o.id, o]));
  const colors = themeColors();
  // The Planapus hub uses the brand color so it picks up vermillion in paper themes.
  const hubColor = colors.brand;

  // PLN-309 — long party names like "Solutions Eng / Security" were
  // hard-truncated to "Solutions Eng / Secur…" and the suffix was lost.
  // Wrap to a second tspan on the nearest boundary (` / ` or ` `) so
  // the full label survives. Falls back to ellipsis only if no
  // sensible boundary exists inside the budget.
  const wrapLabel = (raw) => {
    if (raw.length <= 22) return [raw];
    // Prefer ` / ` (slash separator); fall back to last space.
    const slashIdx = raw.lastIndexOf(" / ", 22);
    if (slashIdx > 6) return [raw.slice(0, slashIdx).trim(), raw.slice(slashIdx).trim()];
    const spaceIdx = raw.lastIndexOf(" ", 22);
    if (spaceIdx > 6) return [raw.slice(0, spaceIdx).trim(), raw.slice(spaceIdx + 1).trim()];
    // Last resort: single-line ellipsis.
    return [raw.slice(0, 21) + "…"];
  };
  const nodeGroup = layout.nodes.map(n => {
    const o = orgs[n.org] || { color: colors.muted };
    const lines = wrapLabel(n.name);
    const uninvolved = involvedSet && !involvedSet.has(n.id);
    const cls = uninvolved ? "lane-node lane-node-uninvolved" : "lane-node";
    const textBody = lines.length === 1
      ? escapeHtml(lines[0])
      : `<tspan x="${n.x}" dy="0">${escapeHtml(lines[0])}</tspan><tspan x="${n.x}" dy="1.15em">${escapeHtml(lines[1])}</tspan>`;
    return `
      <g class="${cls}" data-id="${n.id}">
        <circle cx="${n.x}" cy="${n.y}" r="18" fill="${o.color}22" stroke="${o.color}" stroke-width="2"/>
        <text x="${n.x}" y="${n.y + 36}" fill="${colors.dim}" font-size="12" font-weight="500" text-anchor="middle">${textBody}</text>
      </g>
    `;
  }).join("");

  const hub = layout.hub ? `
    <g>
      <circle cx="${layout.hub.x}" cy="${layout.hub.y}" r="28" fill="${hubColor}22" stroke="${hubColor}" stroke-width="2"/>
      <text x="${layout.hub.x}" y="${layout.hub.y + 8}" fill="${hubColor}" font-size="26" text-anchor="middle">🐙</text>
    </g>
  ` : "";

  let spokes = "";
  if (layout.hub) {
    spokes = layout.nodes.map(n => {
      const uninvolved = involvedSet && !involvedSet.has(n.id);
      const stroke = `${hubColor}${uninvolved ? "1F" : "44"}`;
      return `<line x1="${layout.hub.x}" y1="${layout.hub.y}" x2="${n.x}" y2="${n.y}" stroke="${stroke}" stroke-width="1"/>`;
    }).join("");
  }

  svg.innerHTML = `
    ${spokes}
    <g id="${svgId}-path"></g>
    ${nodeGroup}
    ${hub}
    <g id="${svgId}-callouts"></g>
    <g id="${svgId}-active"></g>
    <g id="${svgId}-token"></g>
  `;
}

// Derive a contextual one-liner for the current handoff so the
// animation reads as a story, not just a moving dot. Per the May 2026
// review feedback: the lines between nodes should explain WHY a step
// is slow, not just animate. We surface three categories:
//   - dropped steps          → "step dropped" callout
//   - long-wait steps        → OOO / weekend callout (uses assignee name)
//   - a deterministic subset → "incorrect data / missing field" callouts
// The subset is keyed off a hash of the step label + index so the same
// step always shows the same callout — visitors who replay don't see a
// different flavor each time, which would read as randomness, not
// narrative.
const HUMAN_FLAVORS = [
  "Returned with incorrect data",
  "Missing required attachment",
  "Wrong addressee on the email thread",
  "Bounced for a missing signature",
  "Stalled waiting on a manager approval",
  "Re-sent because the file was the wrong version",
];

// Deterministic-but-pseudo-random hash of a step's identity. Used both
// to gate "does this step get a callout?" and to pick a flavor — so a
// replay shows the same callouts (narrative consistency) instead of
// rolling fresh each time.
function stepHash(step, idx) {
  const labelStr = (step && step.label) || "";
  let h = 0;
  for (let i = 0; i < labelStr.length; i++) {
    h = (h * 31 + labelStr.charCodeAt(i)) | 0;
  }
  h = (h + idx * 17) | 0;
  return Math.abs(h);
}

function stepCallout(step, idx, mode) {
  if (!step) return null;
  // AI lane: Planapus orchestrates, so we don't surface human-coordination
  // failure modes. The annotation under the lane already calls out the
  // savings; the lane animation reads as smooth on purpose.
  if (mode === "ai") return null;
  // Dropped steps are rare and always carry narrative weight, so they
  // always fire. Everything else rolls a hash gate so most segments
  // stay quiet and the run reads as "a few notable things happened",
  // not "every handoff has commentary".
  if (step.dropped) {
    const who = step.assigneeName || "Someone";
    return `${who} let this one go silent`;
  }
  const hash = stepHash(step, idx);
  // ~30% of non-dropped steps get a callout. Tuned so a typical
  // 12-step run produces ~3-4 callouts — enough to feel narrative,
  // few enough that the chart doesn't read as wall-of-text.
  if (hash % 10 >= 3) return null;
  const latHrs = step.lat || 0;
  const days = latHrs / 24;
  if (days >= 5) {
    const who = step.assigneeName || "Reviewer";
    return `${who} was OOO for ${Math.round(days)} days`;
  }
  if (days >= 2) {
    return "Sat in an inbox over the weekend";
  }
  return HUMAN_FLAVORS[hash % HUMAN_FLAVORS.length];
}

function animateTrace(svgId, proc, layout, trace, totalHrs, clockEl, mode, durationMs) {
  const tokenG = document.getElementById(`${svgId}-token`);
  const activeG = document.getElementById(`${svgId}-active`);
  const pathG = document.getElementById(`${svgId}-path`);
  const calloutG = document.getElementById(`${svgId}-callouts`);
  if (!tokenG || !trace.length) return;
  const colors = themeColors();
  const humanColor = colors.human;
  const aiColor = colors.ai;

  const partyPos = id => layout.nodes.find(n => n.id === id);
  // Tracks which step indices have already been written to the
  // persistent callouts layer. Each step's callout paints exactly once
  // (when the dot is mid-traverse on that segment) and stays painted
  // for the rest of the animation — so by the time the run finishes
  // the visitor sees a stacked record of "everything that went wrong".
  // On replay, renderLaneStatic re-paints svg.innerHTML which wipes
  // the callouts group, so the reset is automatic.
  const paintedCallouts = new Set();
  // Collision tracking — every callout we've laid down so far, as a
  // bounding box {x1,y1,x2,y2} in viewBox units. New callouts that
  // would overlap an existing box try alternate offsets; if no
  // non-overlapping placement is found within ~6 attempts they're
  // skipped entirely. Better to drop a callout than stack illegible
  // text. The dedup set prevents identical callouts ("Priya was OOO
  // for 10 days" twice) from painting at all.
  const paintedBoxes = [];
  const paintedTexts = new Set();
  const start = performance.now();

  function frame(now) {
    const tRaw = (now - start) / durationMs;
    const t = Math.min(1, tRaw);

    let cumStart = 0, cumEnd = 0, idx = 0;
    for (let i = 0; i < trace.length; i++) {
      cumEnd = cumStart + trace[i].lat;
      if (t * totalHrs <= cumEnd) { idx = i; break; }
      cumStart = cumEnd;
      idx = i;
    }
    const step = trace[idx];
    const localT = totalHrs > 0 ? Math.min(1, (t * totalHrs - cumStart) / Math.max(0.001, step.lat)) : 1;
    const fromNode = partyPos(step.from) || layout.nodes[0];
    const toNode = partyPos(step.to) || layout.nodes[0];

    let x, y;
    if (mode === "ai" && layout.hub) {
      if (localT < 0.5) {
        const u = localT / 0.5;
        x = fromNode.x + (layout.hub.x - fromNode.x) * u;
        y = fromNode.y + (layout.hub.y - fromNode.y) * u;
      } else {
        const u = (localT - 0.5) / 0.5;
        x = layout.hub.x + (toNode.x - layout.hub.x) * u;
        y = layout.hub.y + (toNode.y - layout.hub.y) * u;
      }
    } else {
      x = fromNode.x + (toNode.x - fromNode.x) * localT;
      y = fromNode.y + (toNode.y - fromNode.y) * localT;
    }

    if (mode === "human" && pathG) {
      let pathSvg = "";
      for (let i = 0; i < idx; i++) {
        const s = trace[i];
        const f = partyPos(s.from); const tn = partyPos(s.to);
        if (f && tn) {
          pathSvg += `<line x1="${f.x}" y1="${f.y}" x2="${tn.x}" y2="${tn.y}" stroke="${humanColor}" stroke-width="1.5" stroke-opacity="0.55" stroke-linecap="round"/>`;
        }
      }
      pathSvg += `<line x1="${fromNode.x}" y1="${fromNode.y}" x2="${x}" y2="${y}" stroke="${humanColor}" stroke-width="2" stroke-opacity="0.9" stroke-linecap="round"/>`;
      pathG.innerHTML = pathSvg;
    }

    const color = mode === "ai" ? aiColor : humanColor;
    tokenG.innerHTML = `
      <circle cx="${x}" cy="${y}" r="6" fill="${color}" />
      <circle cx="${x}" cy="${y}" r="11" fill="${color}33" />
    `;
    // PLN-310 — labels like "Counter-redline" lose their hyphen at 14px on
    // cream and read as "Counterredline" (audit evidence). Two tweaks:
    // bump to 15px with a 700 weight so the hyphen has weight to render,
    // and replace ASCII `-` with the Unicode non-breaking hyphen `‑`
    // which has a fuller glyph in Public Sans and never breaks lines.
    const labelDisplay = escapeHtml(step.label).replace(/-/g, "‑");
    activeG.innerHTML = `
      <text x="300" y="24" text-anchor="middle" fill="${color}" font-size="15" font-weight="700">
        ${labelDisplay}
      </text>
    `;

    // Persistent callout layer: when the dot is mid-traverse on a step
    // that has a callout, paint a small italic label near that
    // segment. Fires once per step (Set-guarded) and stays for the
    // rest of the run. Collision-avoidance tries multiple offsets
    // before giving up — skipping a callout is better than letting
    // two stack on top of each other.
    if (calloutG && !paintedCallouts.has(idx) && localT > 0.25) {
      const callout = stepCallout(step, idx, mode);
      // Skip if we've already painted this exact text (e.g. two steps
      // assigned to the same "Priya" both rolling the OOO branch).
      if (callout && !paintedTexts.has(callout)) {
        paintedCallouts.add(idx);
        // Estimate the text's bounding box at 11px italic. Public Sans
        // ≈ 5.6px/char average + a couple px padding for the halo.
        const halfW = (callout.length * 5.6) / 2 + 4;
        const halfH = 8;
        const mx = (fromNode.x + toNode.x) / 2;
        const my = (fromNode.y + toNode.y) / 2;
        const dx = toNode.x - fromNode.x;
        const dy = toNode.y - fromNode.y;
        const len = Math.max(1, Math.hypot(dx, dy));
        // Perpendicular unit vector. Prefer the side that points
        // outward from the chart center so the callout sits in the
        // gutter rather than on top of other nodes.
        const cx0 = 300, cy0 = 270;
        let nx = -dy / len, ny = dx / len;
        if ((mx - cx0) * nx + (my - cy0) * ny < 0) {
          nx = -nx;
          ny = -ny;
        }
        // Try increasing perpendicular offsets, then the opposite
        // side. First non-overlapping placement wins.
        const attempts = [
          {mag:  14, sign:  1},
          {mag:  22, sign:  1},
          {mag:  14, sign: -1},
          {mag:  30, sign:  1},
          {mag:  22, sign: -1},
          {mag:  38, sign:  1},
          {mag:  30, sign: -1},
        ];
        let placed = null;
        for (const {mag, sign} of attempts) {
          let cx = mx + nx * mag * sign;
          let cy = my + ny * mag * sign;
          // Clamp inside the 600x540 viewBox with a small inset so
          // text never clips at the edges.
          cx = Math.max(halfW + 4, Math.min(600 - halfW - 4, cx));
          cy = Math.max(halfH + 14, Math.min(540 - halfH - 4, cy));
          const box = {
            x1: cx - halfW, y1: cy - halfH,
            x2: cx + halfW, y2: cy + halfH,
          };
          const overlaps = paintedBoxes.some(b =>
            !(box.x2 < b.x1 || box.x1 > b.x2 || box.y2 < b.y1 || box.y1 > b.y2),
          );
          if (!overlaps) {
            placed = {cx, cy, box};
            break;
          }
        }
        if (placed) {
          paintedBoxes.push(placed.box);
          paintedTexts.add(callout);
          const halo = (
            typeof document !== "undefined"
              ? getComputedStyle(document.documentElement)
                  .getPropertyValue("--bg")
                  .trim()
              : ""
          ) || "#F7F1E0";
          const tx = document.createElementNS("http://www.w3.org/2000/svg", "text");
          tx.setAttribute("x", String(placed.cx));
          tx.setAttribute("y", String(placed.cy));
          tx.setAttribute("text-anchor", "middle");
          tx.setAttribute("font-size", "11");
          tx.setAttribute("font-style", "italic");
          tx.setAttribute("font-weight", "600");
          tx.setAttribute("fill", colors.muted);
          tx.setAttribute("paint-order", "stroke");
          tx.setAttribute("stroke", halo);
          tx.setAttribute("stroke-width", "3");
          tx.setAttribute("stroke-linejoin", "round");
          tx.setAttribute("stroke-opacity", "0.95");
          tx.setAttribute("class", "lane-callout");
          tx.style.opacity = "0";
          tx.style.transition = "opacity 280ms ease-out";
          tx.textContent = callout;
          calloutG.appendChild(tx);
          requestAnimationFrame(() => {
            tx.style.opacity = "0.95";
          });
        }
      }
    }

    clockEl.textContent = `Day ${(t * totalHrs / 24).toFixed(1)}`;

    if (tRaw < 1) requestAnimationFrame(frame);
    else clockEl.textContent = `Day ${(totalHrs / 24).toFixed(1)}`;
  }
  requestAnimationFrame(frame);
}

export function renderLanes(proc, traceH, traceA, opts = {}) {
  const w = 600, h = 540;
  const humanSvg  = opts.humanSvg  || "svg-human";
  const aiSvg     = opts.aiSvg     || "svg-ai";
  const humanClock = opts.humanClock || "clock-human";
  const aiClock    = opts.aiClock    || "clock-ai";

  const hasHuman = traceH && document.getElementById(humanSvg);
  const hasAi    = traceA && document.getElementById(aiSvg);

  const layoutH = hasHuman ? layoutNodes(proc.parties, w, h, "human") : null;
  const layoutA = hasAi    ? layoutNodes(proc.parties, w, h, "ai")    : null;

  if (hasHuman) {
    renderLaneStatic(humanSvg, proc, layoutH, "human", tracePartySet(traceH.trace));
  }
  if (hasAi) {
    renderLaneStatic(aiSvg, proc, layoutA, "ai", tracePartySet(traceA.trace));
  }

  // Synchronize animation duration so the side-by-side race reads as a fair comparison.
  const maxHrs = Math.max(hasHuman ? traceH.hours : 0, hasAi ? traceA.hours : 0, 1);
  const totalMs = opts.totalMs || 8000;
  const factor = totalMs / maxHrs;

  if (hasHuman) {
    animateTrace(humanSvg, proc, layoutH, traceH.trace, traceH.hours,
      document.getElementById(humanClock), "human", traceH.hours * factor);
  }
  if (hasAi) {
    animateTrace(aiSvg, proc, layoutA, traceA.trace, traceA.hours,
      document.getElementById(aiClock), "ai", traceA.hours * factor);
  }
}

// ---------- utility ----------

function escapeHtml(s) {
  if (s == null) return "";
  return String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}
